"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  PILL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { Meter, type MeterTone } from "@/components/tools/admissions/meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyseEssay,
  COMMON_APP_CYCLE,
  COMMON_APP_DEFAULT_TARGET,
  COMMON_APP_MAX_WORDS,
  COMMON_APP_MIN_WORDS,
  COMMON_APP_PROMPTS,
  type CommonAppPrompt,
  clampTarget,
  OUTLINE_STEPS,
  type OutlineStep,
  outlineTargets,
} from "@/lib/tool-kit/admissions/common-app";
import { joinSections } from "@/lib/tool-kit/admissions/counts";
import { downloadText } from "@/lib/tool-kit/download";
import { parseWholeCount } from "@/lib/tool-kit/essay-templates/text";

/** English strings. A translated content file overrides these through `ui`. */
export const COMMON_APP_ESSAY_PLANNER_STRINGS = {
  promptHeading: "1. Choose a prompt",
  promptNote:
    "The seven first-year prompts for the {cycle} application cycle, as Common App words them. Prompt 7 lets you write on any topic.",
  promptLabel: "Prompt {n}",
  prompts: {
    p1: "Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it. If this sounds like you, then please share your story.",
    p2: "The lessons we take from obstacles we encounter can be fundamental to later success. Recount a time when you faced a challenge, setback, or failure. How did it affect you, and what did you learn from the experience?",
    p3: "Reflect on a time when you questioned or challenged a belief or idea. What prompted your thinking? What was the outcome?",
    p4: "Reflect on something that someone has done for you that has made you happy or thankful in a surprising way. How has this gratitude affected or motivated you?",
    p5: "Discuss an accomplishment, event, or realization that sparked a period of personal growth and a new understanding of yourself or others.",
    p6: "Describe a topic, idea, or concept you find so engaging that it makes you lose all track of time. Why does it captivate you? What or who do you turn to when you want to learn more?",
    p7: "Share an essay on any topic of your choice. It can be one you've already written, one that responds to a different prompt, or one of your own design.",
  },
  brainstormHeading: "2. Brainstorm",
  brainstormIntro: "Questions to answer in rough notes before you outline:",
  brainstormQuestions: {
    p1: [
      "What is the background, identity, interest or talent, and why would your application feel incomplete without it?",
      "Which single moment or object shows it best?",
      "How has it changed what you do, not just who you are?",
    ],
    p2: [
      "What was the challenge, in one sentence, and when did it happen?",
      "What did you do in the first week afterwards, in order?",
      "What would you do differently now, and what would you keep?",
    ],
    p3: [
      "Which belief or idea did you question, and whose was it?",
      "What did you read, see or hear that made you doubt it?",
      "What did you decide, and what did it change or cost?",
    ],
    p4: [
      "Who did what for you, and why was it surprising?",
      "What did you do in response, and when?",
      "Where does that gratitude show up in how you treat people now?",
    ],
    p5: [
      "Which event or realization separates your “before” from your “after”?",
      "What did you believe then, and what do you understand now?",
      "Where could a reader see the change in what you actually do?",
    ],
    p6: [
      "What topic makes you lose track of time, and when did that last happen?",
      "When you want to learn more, what or who do you turn to?",
      "What question about it are you still chasing?",
    ],
    p7: [
      "What do you most want an admissions reader to understand that is not elsewhere in your application?",
      "Which single story shows it?",
      "Which of the other six prompts is closest, and does your idea fit as a version of it?",
    ],
  },
  brainstormLabel: "Your brainstorm notes",
  brainstormPlaceholder:
    "Rough notes, lists, half-sentences — nothing here is graded.",
  outlineHeading: "3. Outline",
  targetLabel: "Target length (words)",
  targetHint:
    "Between {min} and {max}. Aim a little under the limit: counters can differ by a few words.",
  steps: {
    scene: "Opening moment",
    context: "Context",
    turn: "The turning point",
    reflection: "What it meant",
    close: "Where it leaves you",
  },
  stepGuidance: {
    scene:
      "Start inside one specific moment — a place, a sound, a line of dialogue — not a general statement.",
    context:
      "Only the background a stranger needs to follow the moment. Cut the rest.",
    turn: "The challenge, decision or change: what happened and what you did about it.",
    reflection:
      "What you understood and how you changed. This is the longest part because it is what an activities list cannot tell the reader.",
    close:
      "Where this leaves you now. Point forward without summarizing the essay.",
  },
  stepLabel: "{step} — about {words}",
  wordCount: "one:{count} word|other:{count} words",
  outlinePlaceholder: "A few lines on what goes here.",
  draftHeading: "4. Draft",
  draftLabel: "Your essay",
  draftPlaceholder: "Write or paste your draft here.",
  statusEmpty: "Start writing to see the count.",
  statusUnder: "{gap} more to reach the {min}-word minimum.",
  statusIn: "Within range, with {gap} to spare before the {max}-word limit.",
  statusOver: "{gap} over the {max}-word limit.",
  statWords: "Words",
  statParagraphs: "Paragraphs",
  statSentences: "Sentences",
  statAverage: "Average sentence",
  statLongest: "Longest sentence",
  statOpenI: "Sentences opening with “I”",
  flagLongest:
    "Your longest sentence runs {words}. Read it aloud; if you run out of breath, split it.",
  flagOpenI:
    "More than half your sentences open with “I”. That is common in a personal essay, but varied openings can read more smoothly.",
  copyDraft: "Copy draft",
  copied: "Copied!",
  checksHeading: "5. Final checks",
  checksNote: "Tick these yourself — only you can judge them.",
  checks: [
    "It answers the prompt I chose (or prompt 7, on purpose).",
    "It opens with a specific moment, not a general statement.",
    "It shows what I thought or learned, not only what happened.",
    "It tells the reader something the rest of my application does not.",
    "I read it aloud and it sounds like me.",
    "Someone I trust has read it, and every sentence is mine.",
  ],
  download: "Download plan and draft (.txt)",
  downloadName: "common-app-essay-plan.txt",
  clear: "Clear everything",
  exportPrompt: "Prompt",
  exportBrainstorm: "Brainstorm",
  exportDraft: "Draft",
  privacy:
    "Everything stays in your browser. Nothing is uploaded or saved, so download or copy your work before you close the page.",
} as const;

type Strings = typeof COMMON_APP_ESSAY_PLANNER_STRINGS;
type Notes = Record<OutlineStep, string>;

const EMPTY_NOTES: Notes = {
  scene: "",
  context: "",
  turn: "",
  reflection: "",
  close: "",
};

function toneForStatus(status: string): MeterTone {
  if (status === "over") {
    return "over";
  }
  if (status === "in") {
    return "ok";
  }
  return status === "under" ? "near" : "idle";
}

export default function CommonAppEssayPlannerWidget() {
  const strings = useToolUi(COMMON_APP_ESSAY_PLANNER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [prompt, setPrompt] = useState<CommonAppPrompt>("p1");
  const [brainstorm, setBrainstorm] = useState("");
  const [targetRaw, setTargetRaw] = useState(String(COMMON_APP_DEFAULT_TARGET));
  const [notes, setNotes] = useState<Notes>(EMPTY_NOTES);
  const [draft, setDraft] = useState("");
  const [ticked, setTicked] = useState<boolean[]>(() =>
    strings.checks.map(() => false)
  );
  const target = clampTarget(
    parseWholeCount(targetRaw) ?? COMMON_APP_DEFAULT_TARGET
  );
  const targets = outlineTargets(target);
  const analysis = useMemo(() => analyseEssay(draft), [draft]);
  const words = (count: number) =>
    tPlural(strings.wordCount, locale, count, { count: fmt(count) });

  const statusText = {
    empty: strings.statusEmpty,
    under: t(strings.statusUnder, {
      gap: words(analysis.toMinimum),
      min: fmt(COMMON_APP_MIN_WORDS),
    }),
    in: t(strings.statusIn, {
      gap: words(analysis.toMaximum),
      max: fmt(COMMON_APP_MAX_WORDS),
    }),
    over: t(strings.statusOver, {
      gap: words(Math.abs(analysis.toMaximum)),
      max: fmt(COMMON_APP_MAX_WORDS),
    }),
  }[analysis.status];

  function download() {
    const text = joinSections([
      {
        heading: `${strings.exportPrompt} ${prompt.slice(1)}`,
        body: strings.prompts[prompt],
      },
      { heading: strings.exportBrainstorm, body: brainstorm },
      ...OUTLINE_STEPS.map((step) => ({
        heading: strings.steps[step],
        body: notes[step],
      })),
      { heading: strings.exportDraft, body: draft },
    ]);
    downloadText(strings.downloadName, text);
  }

  return (
    <div className="space-y-8">
      <PromptPicker onChange={setPrompt} prompt={prompt} strings={strings} />

      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.brainstormHeading}</h2>
        <p className={HINT}>{strings.brainstormIntro}</p>
        <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
          {strings.brainstormQuestions[prompt].map((question) => (
            <li key={question}>{question}</li>
          ))}
        </ul>
        <TextAreaField
          id="caep-brainstorm"
          label={strings.brainstormLabel}
          onChange={setBrainstorm}
          placeholder={strings.brainstormPlaceholder}
          prose
          rows={6}
          value={brainstorm}
        />
      </section>

      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.outlineHeading}</h2>
        <div className="max-w-xs">
          <TextField
            hint={t(strings.targetHint, {
              min: fmt(COMMON_APP_MIN_WORDS),
              max: fmt(COMMON_APP_MAX_WORDS),
            })}
            id="caep-target"
            label={strings.targetLabel}
            numeric
            onChange={setTargetRaw}
            value={targetRaw}
          />
        </div>
        {OUTLINE_STEPS.map((step, index) => (
          <TextAreaField
            hint={strings.stepGuidance[step]}
            id={`caep-step-${step}`}
            key={step}
            label={`${index + 1}. ${t(strings.stepLabel, {
              step: strings.steps[step],
              words: words(targets[step]),
            })}`}
            onChange={(value) =>
              setNotes((current) => ({ ...current, [step]: value }))
            }
            placeholder={strings.outlinePlaceholder}
            prose
            rows={3}
            value={notes[step]}
          />
        ))}
      </section>

      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.draftHeading}</h2>
        <TextAreaField
          id="caep-draft"
          label={strings.draftLabel}
          onChange={setDraft}
          placeholder={strings.draftPlaceholder}
          prose
          rows={16}
          value={draft}
        />
        <Meter
          caption={statusText}
          limit={COMMON_APP_MAX_WORDS}
          marker={COMMON_APP_MIN_WORDS}
          tone={toneForStatus(analysis.status)}
          value={analysis.words}
        />
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statWords}
            value={fmt(analysis.words)}
          />
          <StatCard
            label={strings.statParagraphs}
            value={fmt(analysis.paragraphs)}
          />
          <StatCard
            label={strings.statSentences}
            value={fmt(analysis.sentences)}
          />
          <StatCard
            label={strings.statAverage}
            value={fmt(analysis.averageSentenceWords, {
              maximumFractionDigits: 1,
            })}
          />
          <StatCard
            label={strings.statLongest}
            value={fmt(analysis.longestSentenceWords)}
          />
          <StatCard
            label={strings.statOpenI}
            value={`${fmt(analysis.startsWithIPercent)}%`}
          />
        </StatGrid>
        {analysis.longestSentenceWords > 40 ? (
          <p className={HINT}>
            {t(strings.flagLongest, {
              words: words(analysis.longestSentenceWords),
            })}
          </p>
        ) : null}
        {analysis.startsWithIPercent > 50 ? (
          <p className={HINT}>{strings.flagOpenI}</p>
        ) : null}
        <CopyButton
          copiedLabel={strings.copied}
          disabled={draft.trim() === ""}
          label={strings.copyDraft}
          text={draft.trim()}
        />
      </section>

      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.checksHeading}</h2>
        <p className={HINT}>{strings.checksNote}</p>
        {strings.checks.map((check, index) => (
          <Checkbox
            checked={ticked[index] ?? false}
            key={check}
            label={check}
            onChange={(value) =>
              setTicked((current) =>
                current.map((old, at) => (at === index ? value : old))
              )
            }
          />
        ))}
      </section>

      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_SECONDARY} onClick={download} type="button">
          {strings.download}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setBrainstorm("");
            setNotes(EMPTY_NOTES);
            setDraft("");
            setTicked(strings.checks.map(() => false));
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <p className={HINT}>{strings.privacy}</p>
    </div>
  );
}

function PromptPicker({
  prompt,
  onChange,
  strings,
}: {
  prompt: CommonAppPrompt;
  onChange: (prompt: CommonAppPrompt) => void;
  strings: Strings;
}) {
  return (
    <fieldset className={`${CARD} space-y-3`}>
      <legend className={WIDGET_H2}>{strings.promptHeading}</legend>
      <p className={HINT}>
        {t(strings.promptNote, { cycle: COMMON_APP_CYCLE })}
      </p>
      <div className="grid gap-2">
        {COMMON_APP_PROMPTS.map((id) => (
          <label className={`${PILL} items-start`} key={id}>
            <input
              checked={prompt === id}
              className="mt-1 accent-primary"
              name="caep-prompt"
              onChange={() => onChange(id)}
              type="radio"
              value={id}
            />
            <span className="space-y-1">
              <span className={`block ${WIDGET_H3} text-sm`}>
                {t(strings.promptLabel, { n: id.slice(1) })}
              </span>
              <span className="block text-sm">{strings.prompts[id]}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
